import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, ArrowLeft, Package, Bot, Bell, PawPrint, Mail, Calendar, ChevronDown, ChevronUp, Search, Trash2, Ticket, Check, Undo2, Tag } from 'lucide-react';
import { TagsEditor } from '../components/TagsEditor';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerRow = ({ customer, onClick }) => (
  <div
    className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-[#0FA49C] hover:shadow-sm transition-all"
    onClick={() => onClick(customer.customer_id)}
    data-testid={`customer-${customer.customer_id}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {customer.picture ? (
          <img src={customer.picture} alt="" className="w-10 h-10 rounded-full" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#0FA49C] text-white flex items-center justify-center text-sm font-bold">
            {customer.name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
        <div>
          <p className="font-bold text-[#222] text-sm">{customer.name || 'No name'}</p>
          <p className="text-xs text-[#999] flex items-center gap-1"><Mail className="w-3 h-3" />{customer.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex gap-2">
          {customer.order_count > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">
              <Package className="w-3 h-3" />{customer.order_count}
            </span>
          )}
          {customer.scan_count > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-700">
              <Bot className="w-3 h-3" />{customer.scan_count}
            </span>
          )}
          {customer.dog_profile_count > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700">
              <PawPrint className="w-3 h-3" />{customer.dog_profile_count}
            </span>
          )}
          {customer.alert_count > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
              <Bell className="w-3 h-3" />{customer.alert_count}
            </span>
          )}
        </div>
        <span className="text-xs text-[#bbb]">
          {customer.auth_provider === 'google' ? 'Google' : 'Email'}
        </span>
      </div>
    </div>
  </div>
);

const ExpandableSection = ({ title, icon: Icon, count, color, children, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen ?? count > 0);
  // Re-open when the section actually has content — first render can happen
  // before the detail fetch completes (initial count = 0), which would
  // otherwise leave the panel collapsed even once items arrive.
  const prevCount = React.useRef(count);
  React.useEffect(() => {
    if (prevCount.current === 0 && count > 0) setOpen(true);
    prevCount.current = count;
  }, [count]);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-sm text-[#222]">{title}</span>
          <span className="text-xs text-[#999] bg-gray-100 px-2 py-0.5 rounded-full font-bold">{count}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#999]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
      </button>
      {open && <div className="border-t border-gray-100 p-4 space-y-3">{children}</div>}
    </div>
  );
};

const OrderItem = ({ order }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <div className="flex items-center justify-between mb-1.5">
      <p className="font-semibold text-sm text-[#222]">{order.box_size} Sample Box</p>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${order.processed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
        {order.processed ? 'Processed' : 'Pending'}
      </span>
    </div>
    <div className="flex flex-wrap gap-1 mb-1">
      {order.selected_products?.map((pid, i) => (
        <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-white border text-[#666] font-medium">{pid}</span>
      ))}
    </div>
    <p className="text-[10px] text-[#999]">
      {new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
      {order.promo_code_used && <span className="ml-2 text-[#0FA49C]">Promo: {order.promo_code_used}</span>}
    </p>
  </div>
);

const ScanItem = ({ scan }) => {
  const [expanded, setExpanded] = useState(false);
  const bag = scan.scanned_bag || {};
  return (
    <div className="bg-gray-50 rounded-lg overflow-hidden">
      <div className="p-3 cursor-pointer hover:bg-gray-100" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-sm text-[#222]">{bag.brand} — {bag.product_name}</p>
            <p className="text-[10px] text-[#999]">
              {new Date(scan.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              {' · '}{scan.recommended_products?.length || 0} matches
              {bag.bag_weight && ` · ${bag.bag_weight}`}
            </p>
          </div>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#999]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#999]" />}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-200 pt-2">
          {bag.type && <p className="text-xs text-[#666]"><span className="font-semibold">Type:</span> {bag.type.replace(/_/g, ' ')}</p>}
          {bag.lifestage && <p className="text-xs text-[#666]"><span className="font-semibold">Lifestage:</span> {bag.lifestage}</p>}
          {bag.protein_source && <p className="text-xs text-[#666]"><span className="font-semibold">Protein:</span> {bag.protein_source}</p>}
          {scan.explanation && <p className="text-xs text-[#444] bg-[#0FA49C]/5 rounded p-2">{scan.explanation}</p>}
          {scan.recommended_products?.map((rec, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2">
              <div className="w-6 h-6 rounded-full bg-[#0FA49C] text-white flex items-center justify-center text-[10px] font-bold">{rec.match_score}%</div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#222]">{rec.product_name} <span className="text-[#0FA49C]">({rec.product_id})</span></p>
                {rec.why_selected && <p className="text-[10px] text-[#666]">{rec.why_selected}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DogProfileItem = ({ profile }) => {
  const [expanded, setExpanded] = useState(false);
  const dog = profile.dog_profile || {};
  return (
    <div className="bg-gray-50 rounded-lg overflow-hidden">
      <div className="p-3 cursor-pointer hover:bg-gray-100" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PawPrint className="w-4 h-4 text-purple-500" />
            <div>
              <p className="font-semibold text-sm text-[#222]">{dog.dog_name}</p>
              <p className="text-[10px] text-[#999]">
                {dog.breed} · {dog.age}{dog.weight && ` · ${dog.weight}`}
                {' · '}{profile.recommended_products?.length || 0} matches
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#999]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#999]" />}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-200 pt-2">
          {dog.activity_level && <p className="text-xs text-[#666]"><span className="font-semibold">Activity:</span> {dog.activity_level}</p>}
          {dog.sensitivities && <p className="text-xs text-[#666]"><span className="font-semibold">Sensitivities:</span> {dog.sensitivities}</p>}
          {dog.current_food && <p className="text-xs text-[#666]"><span className="font-semibold">Current food:</span> {dog.current_food}</p>}
          {profile.summary && <p className="text-xs text-[#444] bg-purple-50 rounded p-2">{profile.summary}</p>}
          {profile.recommended_products?.map((rec, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2">
              <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-[10px] font-bold">{rec.match_score}%</div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#222]">{rec.product_name} <span className="text-purple-600">({rec.product_id})</span></p>
                {rec.why_selected && <p className="text-[10px] text-[#666]">{rec.why_selected}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AlertItem = ({ alert }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <div className="flex items-center justify-between mb-1">
      <p className="font-semibold text-sm text-[#222]">{alert.brand} — {alert.product_name}</p>
      {alert.verified && <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Verified</span>}
    </div>
    <p className="text-[10px] text-[#999]">
      {new Date(alert.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
      {alert.current_price != null && ` · Pays £${Number(alert.current_price).toFixed(2)}`}
      {alert.bag_weight && ` · ${alert.bag_weight}`}
      {alert.cheapest_price && ` · Found £${Number(alert.cheapest_price).toFixed(2)} at ${alert.cheapest_site}`}
      {alert.notified_at && ' · Notified'}
    </p>
  </div>
);

const AdminVoucherItem = ({ voucher, onToggle }) => {
  const isUsed = !!voucher.used_at;
  return (
    <div
      className={`rounded-lg p-3 flex items-center gap-3 ${isUsed ? 'bg-gray-50' : 'bg-emerald-50'}`}
      data-testid={`admin-voucher-${voucher.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className={`font-semibold text-sm ${isUsed ? 'text-gray-500 line-through' : 'text-[#1a1a1a]'}`}>
            {voucher.label}
          </p>
          {voucher.source && (
            <span className="text-[9px] uppercase tracking-widest font-bold text-[#0FA49C] bg-white px-1.5 py-0.5 rounded">
              {voucher.source}
            </span>
          )}
          {isUsed && (
            <span className="text-[9px] uppercase tracking-widest font-bold text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
              Used
            </span>
          )}
        </div>
        {voucher.description && (
          <p className="text-[11px] text-[#888] truncate">{voucher.description}</p>
        )}
        {voucher.claim_at && (
          <p className="text-[10px] text-[#0FA49C] font-bold uppercase tracking-widest mt-0.5" data-testid={`admin-voucher-claim-at-${voucher.id}`}>
            Claim at {voucher.claim_at.replace(/^https?:\/\//, '')}
          </p>
        )}
        <p className="text-[10px] text-[#aaa] mt-0.5">
          Earned {new Date(voucher.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          {isUsed && ` · Used ${new Date(voucher.used_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`}
        </p>
      </div>
      {voucher.code && (
        <span className="font-mono text-xs bg-[#a0da39] text-[#1f3d2f] font-black px-2 py-1 rounded tracking-widest flex-shrink-0">
          {voucher.code}
        </span>
      )}
      <Button
        size="sm"
        variant={isUsed ? 'outline' : 'default'}
        onClick={onToggle}
        className={isUsed ? 'text-[#666]' : 'bg-[#1f6d62] hover:bg-[#14544b] text-white'}
        data-testid={`admin-voucher-toggle-${voucher.id}`}
      >
        {isUsed ? <><Undo2 className="w-3.5 h-3.5 mr-1" /> Unmark</> : <><Check className="w-3.5 h-3.5 mr-1" /> Mark used</>}
      </Button>
    </div>
  );
};


export const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers`, { headers: getAuthHeader() });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const selectCustomer = async (customerId) => {
    setSelectedId(customerId);
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, { headers: getAuthHeader() });
      setDetail(response.data);
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleVoucherUsed = async (voucher) => {
    if (!detail?.customer) return;
    const cid = detail.customer.customer_id;
    const wasUsed = !!voucher.used_at;
    const action = wasUsed ? 'mark-unused' : 'mark-used';
    try {
      const res = await axios.post(
        `${API}/admin/customers/${cid}/vouchers/${voucher.id}/${action}`,
        {},
        { headers: getAuthHeader() },
      );
      // Optimistic update on the loaded detail so the UI reflects instantly
      setDetail(prev => ({
        ...prev,
        customer: {
          ...prev.customer,
          vouchers: (prev.customer.vouchers || []).map(v =>
            v.id === voucher.id ? { ...v, used_at: wasUsed ? null : res.data.used_at } : v
          ),
        },
      }));
      toast.success(wasUsed ? 'Marked unused' : 'Marked as used');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update voucher');
    }
  };

  const deleteCustomer = async (customerId, name) => {
    try {
      await axios.delete(`${API}/admin/customers/${customerId}`, { headers: getAuthHeader() });
      setCustomers(customers.filter(c => c.customer_id !== customerId));
      if (selectedId === customerId) { setSelectedId(null); setDetail(null); }
      setConfirmDelete(null);
      toast.success(`${name || 'Customer'} deleted`);
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  const ConfirmDialog = () => confirmDelete ? (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDelete(null)}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete-dialog">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="w-6 h-6 text-red-600" />
        </div>
        <h3 className="font-bold text-lg text-[#222] mb-2">Delete Customer?</h3>
        <p className="text-sm text-[#666] mb-6">
          Are you sure you want to delete <strong>{confirmDelete.name}</strong> and their dog profiles? This cannot be undone.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-btn">Cancel</Button>
          <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl" onClick={() => deleteCustomer(confirmDelete.id, confirmDelete.name)} data-testid="confirm-delete-btn">Delete</Button>
        </div>
      </div>
    </div>
  ) : null;

  // Detail view
  if (selectedId && detail) {
    const c = detail.customer;
    return (
      <div className="space-y-4" data-testid="customer-detail">
        <ConfirmDialog />
        <button
          onClick={() => { setSelectedId(null); setDetail(null); }}
          className="flex items-center gap-1.5 text-sm text-[#0FA49C] font-semibold hover:underline"
          data-testid="back-to-list"
        >
          <ArrowLeft className="w-4 h-4" /> Back to customers
        </button>

        {/* Customer header */}
        <div className="bg-[#0FA49C] rounded-2xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {c.picture ? (
                <img src={c.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white/30" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                  {c.name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div>
                <h3 className="font-bold text-xl">{c.name || 'No name'}</h3>
                <p className="text-sm text-white/80">{c.email}</p>
                <p className="text-xs text-white/60 mt-1">
                  Joined {new Date(c.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {' · '}{c.auth_provider === 'google' ? 'Google login' : 'Email login'}
                </p>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDelete({ id: c.customer_id, name: c.name }); }}
              className="p-2.5 bg-white/10 hover:bg-red-500 rounded-xl transition-colors text-white/70 hover:text-white"
              title="Delete customer"
              data-testid="delete-customer-btn"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loadingDetail ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]" />
          </div>
        ) : (
          <div className="space-y-3">
            <ExpandableSection
              title="Tags"
              icon={Tag}
              count={(detail.customer.tags || []).length}
              color="bg-[#1f6d62]"
              defaultOpen
            >
              <TagsEditor
                customerId={detail.customer.customer_id}
                initialTags={detail.customer.tags || []}
                onChange={(newTags) => setDetail(prev => ({
                  ...prev,
                  customer: { ...prev.customer, tags: newTags },
                }))}
              />
            </ExpandableSection>

            <ExpandableSection title="Orders" icon={Package} count={detail.orders.length} color="bg-blue-500">
              {detail.orders.length === 0 ? (
                <p className="text-xs text-[#999] text-center py-4">No orders</p>
              ) : detail.orders.map(o => <OrderItem key={o.id} order={o} />)}
            </ExpandableSection>

            <ExpandableSection
              title="Vouchers"
              icon={Ticket}
              count={(detail.customer.vouchers || []).length}
              color="bg-[#0FA49C]"
            >
              {(!detail.customer.vouchers || detail.customer.vouchers.length === 0) ? (
                <p className="text-xs text-[#999] text-center py-4">No vouchers</p>
              ) : (
                detail.customer.vouchers.map(v => (
                  <AdminVoucherItem key={v.id} voucher={v} onToggle={() => toggleVoucherUsed(v)} />
                ))
              )}
            </ExpandableSection>

            <ExpandableSection title="Ted AI Scans" icon={Bot} count={detail.scans.length} color="bg-[#0FA49C]">
              {detail.scans.length === 0 ? (
                <p className="text-xs text-[#999] text-center py-4">No scans</p>
              ) : detail.scans.map(s => <ScanItem key={s.id} scan={s} />)}
            </ExpandableSection>

            <ExpandableSection title="Dog Profiles" icon={PawPrint} count={detail.dog_profiles.length} color="bg-purple-500">
              {detail.dog_profiles.length === 0 ? (
                <p className="text-xs text-[#999] text-center py-4">No dog profiles</p>
              ) : detail.dog_profiles.map(p => <DogProfileItem key={p.id} profile={p} />)}
            </ExpandableSection>

            <ExpandableSection title="Price Alerts" icon={Bell} count={detail.alerts.length} color="bg-amber-500">
              {detail.alerts.length === 0 ? (
                <p className="text-xs text-[#999] text-center py-4">No price alerts</p>
              ) : detail.alerts.map(a => <AlertItem key={a.id} alert={a} />)}
            </ExpandableSection>

            <ExpandableSection
              title="Events Attended"
              icon={Calendar}
              count={(detail.events_attended || []).length}
              color="bg-emerald-500"
            >
              {!(detail.events_attended || []).length ? (
                <p className="text-xs text-[#999] text-center py-4">No activity events yet</p>
              ) : detail.events_attended.map(ev => (
                <div
                  key={ev.event_id}
                  className="flex items-center gap-3 p-3 border-b last:border-b-0 bg-white rounded-lg"
                  data-testid={`customer-event-${ev.event_id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#222] truncate">{ev.name}</p>
                    <p className="text-xs text-[#666]">
                      {(ev.activity_type || '').replace('_', ' ')} ·{' '}
                      {ev.event_date
                        ? new Date(ev.event_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : ''}
                    </p>
                    {ev.notes && <p className="text-xs text-[#999] italic mt-0.5 truncate">{ev.notes}</p>}
                  </div>
                </div>
              ))}
            </ExpandableSection>
          </div>
        )}
      </div>
    );
  }

  // Customer list view
  return (
    <div className="space-y-4" data-testid="customer-management">
      <ConfirmDialog />
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-[#222]">
          Customer Accounts <span className="text-[#999] font-normal text-sm">({customers.length})</span>
        </h3>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email..."
            className="pl-10"
            data-testid="customer-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-[#999]">{search ? 'No customers match your search' : 'No customer accounts yet'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <CustomerRow key={c.customer_id} customer={c} onClick={selectCustomer} />
          ))}
        </div>
      )}
    </div>
  );
};
